<script setup lang="ts">
const localePath = useLocalePath()
const { t, setLocale, locale } = useI18n()
const { data } = useAuth()

if (locale.value != data.value.language) {
  /**
   * Set the locale to the user's preferred language
   */
  await setLocale(data.value.language)
}

const employer_store = useEmployerStore()
employer_store.all = toRaw(data.value.employers)
employer_store.current = toRaw(data.value.employers[0])

const employer_manager = employer_store.current.employee_type == 1 || employer_store.current.employee_type == 2;
const general_moderator = data.value.user_type == 1 || data.value.user_type == 2;
const is_moderator = employer_manager || general_moderator

const links = computed(() => {
  if (is_moderator) {
    return [{
      id: 'dashboard-home-link',
      exact: true,
      label: t('general.home'),
      icon: 'i-heroicons-home',
      to: localePath('/dashboard'),
      labelClass: 'dashboard-home-link',
      tooltip: {
        text: t('general.home'),
        shortcuts: ['G', 'H']
      }
    }, {
      id: 'my_shifts',
      label: t('shift.my_shifts'),
      icon: 'i-solar-checklist-minimalistic-outline',
      to: localePath('/dashboard/my_shifts'),
      labelClass: 'dashboard-my-shifts-link',
      tooltip: {
        text: t('shift.my_shifts'),
        shortcuts: ['G', 'S']
      }
    }, {
      id: 'daily_shifts',
      label: t('shift.daily_shifts'),
      icon: 'i-solar-clipboard-list-outline',
      to: localePath('/dashboard/daily_shifts'),
      labelClass: 'dashboard-daily-shifts-link',
      tooltip: {
        text: t('shift.daily_shifts'),
        shortcuts: ['G', 'S']
      }
    }, {
      id: 'pending_approvals',
      label: t('shift.awaiting_approval'),
      icon: 'i-solar-clipboard-check-outline',
      to: localePath('/dashboard/pending_approvals'),
      labelClass: 'dashboard-pending-approvals-link',
      tooltip: {
        text: t('shift.awaiting_approval'),
        shortcuts: ['G', 'W']
      }
    }, {
      id: 'total_hours',
      label: t('shift.total_hours'),
      icon: 'i-vaadin-calc',
      to: localePath('/dashboard/total_hours'),
      labelClass: 'dashboard-total-hours-link',
      tooltip: {
        text: t('shift.total_hours'),
        shortcuts: ['G', 'T']
      }
    }, {
      id: 'task',
      label: t('general.tasks'),
      icon: 'i-heroicons-briefcase',
      to: localePath('/dashboard/task'),
      labelClass: 'dashboard-task-link',
      tooltip: {
        text: t('general.tasks'),
        shortcuts: ['G', 'U']
      }
    }, {
      id: 'ad_hoc_work',
      label: t('adhoc.page_title'),
      icon: 'i-heroicons-plus-circle',
      to: localePath('/dashboard/ad-hoc-work'),
      labelClass: 'dashboard-adhoc-work-link',
      tooltip: {
        text: t('adhoc.page_title'),
        shortcuts: ['G', 'A']
      }
    }]
  } else {
    return [{
      id: 'home',
      exact: true,
      label: t('general.home'),
      icon: 'i-heroicons-home',
      to: localePath('/dashboard'),
      tooltip: {
        text: t('general.home'),
        shortcuts: ['G', 'H']
      }
    }, {
      id: 'my_shifts',
      label: t('shift.my_shifts'),
      icon: 'i-solar-checklist-minimalistic-outline',
      to: localePath('/dashboard/my_shifts'),
      labelClass: 'dashboard-my-shifts-link',
      tooltip: {
        text: t('shift.my_shifts'),
        shortcuts: ['G', 'S']
      }
    }, {
      id: 'ad_hoc_work',
      label: t('adhoc.page_title'),
      icon: 'i-heroicons-plus-circle',
      to: localePath('/dashboard/ad-hoc-work'),
      labelClass: 'dashboard-adhoc-work-link',
      tooltip: {
        text: t('adhoc.page_title'),
        shortcuts: ['G', 'A']
      }
    }]
  }
})

const definitions = computed(() => [{
  id: 'users',
  label: t('general.employees'),
  icon: 'i-heroicons-user-group',
  to: localePath('/dashboard/users'),
  tooltip: {
    text: t('general.employees'),
    shortcuts: ['G', 'U']
  }
}, {
  id: 'companies',
  label: t('general.companies'),
  icon: 'i-heroicons-building-office',
  to: localePath('/dashboard/companies'),
  tooltip: {
    text: t('general.companies'),
    shortcuts: ['G', 'U']
  }
}, {
  id: 'address',
  label: t('general.address'),
  icon: 'i-heroicons-map-pin',
  to: localePath('/dashboard/address'),
  tooltip: {
    text: t('general.address'),
    shortcuts: ['G', 'U']
  }
}, {
  id: 'employer-settings',
  label: t('general.employer_settings'),
  icon: 'i-heroicons-building-storefront',
  to: localePath('/dashboard/employer-settings'),
  tooltip: {
    text: t('general.employer_settings'),
    shortcuts: ['G', 'U']
  }
}])

const admin_links = computed(() => [{
  id: 'users',
  label: t('general.employees'),
  icon: 'i-heroicons-user-group',
  to: localePath('/dashboard/admin/users'),
  tooltip: {
    text: t('general.employees'),
  }
},])


const groups = [{
  key: 'links',
  label: 'Go to',
  commands: links.value.map((link: any) => ({ ...link, shortcuts: link.tooltip?.shortcuts }))
}, {
  key: 'code',
  label: 'Code',
  commands: []
}]

const title = 'ShiftGo'
const description = 'ShiftGo is shift management platform.'

// Use comprehensive SEO setup
useSEO({
  title,
  description,
  keywords: 'shift management, employee scheduling, work schedule, team management, time tracking',
  ogImage: 'https://shiftgo.dk/dashboard-preview.jpg'
})

// Add organization structured data
useStructuredData('Organization', {})

</script>

<template>
  <UDashboardLayout data-cy="dashboard-layout">
    <UDashboardPanel
      data-cy="dashboard-navbar"
      :width="250"
      :resizable="{ min: 200, max: 300 }"
      collapsible
      collapse-on-outside-click
    >
      <UDashboardNavbar
        class="!border-transparent"
        :ui="{ left: 'flex-1' }">
        <template #left>
          <EmployerDropdown />
          <LocaleSelector />
        </template>

        <template #toggle>
          <UDashboardNavbarToggle icon="i-heroicons-x-mark" />
        </template>
      </UDashboardNavbar>

      <UDashboardSidebar>
        <template #header>
          <UDashboardSearchButton :label="t('general.search')" />
        </template>

        <UDashboardSidebarLinks :links="links" />

        <UDivider v-if="is_moderator"  />
        <span v-if="is_moderator" class="text-center text-gray-700 font-semibold"> {{ t('general.definitions') }} </span>
        <UDashboardSidebarLinks v-if="is_moderator" :links="definitions"  />

        <UDivider v-if="general_moderator"  />
        <span v-if="general_moderator" class="text-center text-gray-700 font-semibold"> {{ t('general.admin') }} </span>
        <UDashboardSidebarLinks v-if="general_moderator" :links="admin_links"  />

        <UDivider />

        <div class="flex-1" />

        <UDivider class="sticky bottom-0" />

        <template #footer>
          <!-- ~/components/UserDropdown.vue -->
          <UserDropdown />
        </template>
      </UDashboardSidebar>
    </UDashboardPanel>

    <slot />

    <!-- ~/components/HelpSlideover.vue -->
    <HelpSlideover />
    <!-- ~/components/NotificationsSlideover.vue -->
    <NotificationsSlideover />

    <ClientOnly>
      <LazyUDashboardSearch :groups="groups" :hide-color-mode="true" />
    </ClientOnly>
  </UDashboardLayout>
</template>
